import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button, Checkbox } from '@cargomatic/ui';
import { formatCommittmentCell, formatCreatedAtCell, formatCustomerCell, formatCustomerRateConditionCell, formatCustomerTypeCell, formatEquipmenSizeRateConditionCell, formatEquipmenTypeRateConditionCell, formatHazmatRateConditionCell, formatOrderTypeRateConditionCell, formatRateCell, formatReversibleCell, formatSequenceCell, formatStatusCell, formatSteamshipLineRateConditionCell, formatUpdatedAtCell, formatValidityEnd, formatValidityStart, } from './formatters';
import { RATE_CONDITION_COLUMN_PREFIX } from 'constants/LaneRates';
const SortableColumn = ({ children, column }) => {
    const sortDir = column.getIsSorted();
    return (React.createElement(Button, { variant: "ghost", className: "px-0", onClick: () => column.toggleSorting(sortDir === 'asc') },
        children,
        !sortDir && React.createElement(ArrowUpDown, { className: "ml-2 h-4 w-4" }),
        sortDir === 'asc' && React.createElement(ArrowUp, { className: "ml-2 h-4 w-4" }),
        sortDir === 'desc' && React.createElement(ArrowDown, { className: "ml-2 h-4 w-4" })));
};
export const getColumns = () => {
    const { t } = useTranslation();
    return [
        {
            id: 'select',
            header: ({ table }) => {
                let checked;
                if (table.getIsAllPageRowsSelected()) {
                    checked = true;
                }
                else if (table.getIsSomePageRowsSelected()) {
                    checked = 'indeterminate';
                }
                else {
                    checked = false;
                }
                return (React.createElement(Checkbox, { checked: checked, onCheckedChange: (value) => table.toggleAllPageRowsSelected(!!value), "aria-label": "Select all" }));
            },
            cell: ({ row }) => (React.createElement(Checkbox, { checked: row.getIsSelected(), onCheckedChange: (value) => row.toggleSelected(!!value), "aria-label": "Select row" })),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: 'customerType',
            header: ({ column }) => {
                return (React.createElement(SortableColumn, { column: column }, t('CUSTOMER_TYPE')));
            },
            cell: formatCustomerTypeCell,
        },
        {
            id: 'sequenceDisplay',
            header: () => t('SEQUENCE'),
            cell: formatSequenceCell,
        },
        {
            accessorKey: 'customerName',
            header: ({ column }) => {
                return (React.createElement("div", { className: "min-w-48" },
                    React.createElement(SortableColumn, { column: column }, t('CUSTOMER'))));
            },
            cell: formatCustomerCell,
        },
        {
            accessorKey: 'origin',
            header: ({ column }) => {
                return (React.createElement("div", { className: "min-w-32" },
                    React.createElement(SortableColumn, { column: column }, t('ORIGIN'))));
            },
        },
        {
            accessorKey: 'origin',
            header: ({ column }) => {
                return (React.createElement("div", { className: "min-w-32" },
                    React.createElement(SortableColumn, { column: column }, t('DESTINATION'))));
            },
        },
        {
            accessorKey: 'rate',
            header: ({ column }) => {
                return (React.createElement("div", { className: "flex justify-end" },
                    React.createElement(SortableColumn, { column: column, className: "text-right" }, t('RATE'))));
            },
            cell: formatRateCell,
        },
        {
            id: `${RATE_CONDITION_COLUMN_PREFIX}Customer`,
            header: () => React.createElement("div", { className: "w-36" }, t('CUSTOMER')),
            cell: formatCustomerRateConditionCell,
        },
        {
            id: `${RATE_CONDITION_COLUMN_PREFIX}EquipmentType`,
            header: () => t('EQUIPMENT'),
            cell: formatEquipmenTypeRateConditionCell,
        },
        {
            id: `${RATE_CONDITION_COLUMN_PREFIX}EquipmentSize`,
            header: () => (React.createElement("div", { className: "text-right min-w-20" }, t('EQUIP_SIZE'))),
            cell: formatEquipmenSizeRateConditionCell,
        },
        {
            id: `${RATE_CONDITION_COLUMN_PREFIX}Hazmat`,
            header: () => t('HAZARDOUS'),
            cell: formatHazmatRateConditionCell,
        },
        {
            id: `${RATE_CONDITION_COLUMN_PREFIX}OrderType`,
            header: () => React.createElement("div", { className: "min-w-20" }, t('ORDER_TYPE')),
            cell: formatOrderTypeRateConditionCell,
        },
        {
            accessorKey: `${RATE_CONDITION_COLUMN_PREFIX}SteamshipOrRailroad`,
            header: () => t('STEAMSHIP_RAILROAD'),
            cell: formatSteamshipLineRateConditionCell,
        },
        {
            accessorKey: 'reversible',
            header: () => React.createElement("div", { className: "text-right" },
                t('REVERSIBLE'),
                "?"),
            cell: formatReversibleCell,
        },
        {
            accessorKey: 'commitment',
            header: () => React.createElement("div", { className: "text-right" }, t('COMMITMENT')),
            cell: formatCommittmentCell,
        },
        {
            accessorKey: 'effectiveStart',
            header: () => (React.createElement("div", { className: "min-w-24 text-right" }, t('VALIDITY_START'))),
            cell: formatValidityStart,
        },
        {
            accessorKey: 'effectiveEnd',
            header: () => (React.createElement("div", { className: "min-w-24 text-right" }, t('VALIDITY_END'))),
            cell: formatValidityEnd,
        },
        {
            accessorKey: 'status',
            header: () => t('STATUS'),
            cell: formatStatusCell,
        },
        {
            accessorKey: 'laneRatesUpdatedAt',
            header: () => t('UPDATED'),
            cell: formatUpdatedAtCell,
        },
        {
            accessorKey: 'laneRatesCreatedAt',
            header: () => t('CREATED'),
            cell: formatCreatedAtCell,
        },
    ];
};
